import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  CompletedQueryItem,
  CompletedQueryListRequest,
} from '../../../models/api/query/query-completed';
import { Action } from '../../../models/core/action';
import { BaseComponent } from '../../base/base.component';
import { ActionButtonComponent } from '../../core/buttons/action-button.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { ToolbarButtonComponent } from '../../core/buttons/toolbar-button.component';
import { SelectComponent } from '../../core/controls/select.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import { EmptyStateComponent } from '../../core/panels/empty-state.component';
import { MenuComponent } from '../../core/panels/menu.component';
import { ProgressbarComponent } from '../../core/labels/progressbar.component';
import { ColumnComponent } from '../../core/table/column.component';
import { TableComponent } from '../../core/table/table.component';
import { Snackbar } from '../../../services/snackbar/snackbar.service';
import { QUERY_LIST_PAGE_SIZE_OPTIONS } from '../query-list-page-size-options';

@Component({
  selector: 'app-query-completed',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TableComponent,
    ColumnComponent,
    ButtonComponent,
    ActionButtonComponent,
    ToolbarButtonComponent,
    SelectComponent,
    ActionRowComponent,
    EmptyStateComponent,
    MenuComponent,
    ProgressbarComponent,
  ],
  template: `
    <div>
      @if (retentionNotice) {
        <div
          class="tw-bg-[var(--sed-open-grey-color)] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-3 tw-mb-4 tw-flex tw-items-center tw-gap-2"
        >
          <i class="fa-light fa-circle-info" aria-hidden="true"></i>
          {{ retentionNotice }}
        </div>
      }

      <div
        class="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-end tw-gap-2 sm:tw-gap-0 tw-mb-4"
      >
        <app-select
          class="tw-w-full sm:tw-w-1/3 tw-min-w-0"
          [control]="requestedWithinControl"
          label="Requested Within"
          [options]="dateRangeOptions"
          optionLabel="label"
          optionValue="value"
          [filter]="false"
          [showClear]="false"
        />
        <app-button
          class="tw-w-full sm:tw-w-1/3 tw-pb-2 tw-self-end"
          label="Search"
          icon="fa-light fa-search"
          (click)="search()"
        />
      </div>

      <div class="tw-flex tw-flex-row tw-w-full">
        @if (loading) {
          <app-empty-state
            height="300px"
            icon="fa-light fa-circle-check"
            label="Loading completed queries"
            message="Retrieving completed queries"
            class="tw-w-full"
          >
            <div class="tw-py-4">
              <app-progressbar />
            </div>
          </app-empty-state>
        } @else {
          <app-table
            class="tw-w-full"
            [dataSource]="completedQueries"
            [showRefresh]="true"
            (refresh)="loadCompletedQueries(true)"
            [showSelection]="true"
            [selectionKey]="'completedRowKey'"
            [showFilter]="true"
            [sortField]="sortField"
            [sortOrder]="sortOrder"
            recordLabel="Completed Query"
            recordLabelPlural="Completed Queries"
            dataKey="completedRowKey"
            [pageSize]="pageSize"
            [pageSizeOptions]="pageSizeOptions"
            [persistPaginationSettings]="true"
            tableId="query-completed-queries"
            (selectedEvent)="onSelectionChange($event)"
          >
            <app-column name="requestedDate" label="Requested" [sort]="true">
              <ng-template #cell let-row>
                {{ row.requestedDate | date: 'short' }}
              </ng-template>
            </app-column>
            <app-column name="name" label="Name" [sort]="true" />
            <app-column name="description" label="Description" [sort]="true" />
            <app-column name="pages" label="Pages" />
            <app-column name="fileSize" label="Size" />
            <app-column name="format" label="Format" />
            <app-column name="owner" label="Owner" [sort]="true" />
            <app-column name="actions" label="Actions">
              <ng-template #cell let-row>
                <span class="md:tw-hidden">
                  <app-action-button
                    [menu]="rowMenu"
                    [row]="row"
                    (click)="openRowMenu($event, row)"
                  />
                </span>
                <div class="tw-hidden md:tw-flex tw-gap-1">
                  <app-button
                    label="Cover Page"
                    icon="fa-light fa-file"
                    emphasis="Low"
                    (click)="downloadCoverPage(row)"
                  />
                  <app-button
                    label="Report"
                    icon="fa-light fa-download"
                    (click)="downloadResult(row)"
                  />
                  @if (row.isOwner) {
                    <app-toolbar-button
                      type="Compact"
                      icon="fa-light fa-trash-can"
                      ariaLabel="Delete"
                      (click)="deleteQuery(row)"
                    />
                  }
                </div>
              </ng-template>
            </app-column>
          </app-table>
        }
      </div>

      <app-menu [items]="rowMenuItems" #rowMenu (click)="onRowAction($event)" />

      @if (selectedQueries.length > 0) {
        <app-action-row class="tw-mt-4">
          <span left1>{{ selectedQueries.length }} selected</span>
          @if (selectedQueries.length === 1) {
            <app-button
              right1
              label="Cover Page"
              icon="fa-light fa-file"
              emphasis="Low"
              (click)="downloadCoverPage(selectedQueries[0])"
            />
          }
          @if (selectedQueries.length === 1) {
            <app-button
              right2
              label="Report"
              icon="fa-light fa-download"
              emphasis="Low"
              (click)="downloadResult(selectedQueries[0])"
            />
          }
          @if (selectedOwnedQueries.length > 0) {
            <app-button
              right3
              label="Delete Selected"
              icon="fa-light fa-trash-can"
              emphasis="Low"
              (click)="deleteSelected()"
            />
          }
        </app-action-row>
      }
    </div>
  `,
  styles: ``,
})
export class QueryCompletedComponent extends BaseComponent implements OnInit {
  private readonly snackbar = inject(Snackbar);

  @ViewChild('rowMenu')
  rowMenu: MenuComponent;

  /** Shown when completed-query download APIs fail (handled locally; no global 500 toast / console). */
  private static readonly invalidServerFileMessage =
    'The file on the server is not valid and could not be downloaded.';

  completedQueries: CompletedQueryItem[] = [];
  selectedQueries: CompletedQueryItem[] = [];
  loading = false;
  totalCount = 0;
  pageSize = 10;
  pageSizeOptions = [...QUERY_LIST_PAGE_SIZE_OPTIONS];
  retentionNotice = '';
  sortField = 'requestedDate';
  sortOrder = -1;

  requestedWithinControl = new FormControl('30d');

  get selectedOwnedQueries(): CompletedQueryItem[] {
    return this.selectedQueries.filter((q) => q.isOwner);
  }

  dateRangeOptions = [
    { label: '30 Days', value: '30d' },
    { label: '60 Days', value: '60d' },
    { label: '6 Months', value: '6mo' },
    { label: '1 Year', value: '1yr' },
    { label: 'All', value: 'all' },
  ];

  rowMenuItems: Action[] = [];

  getRowActions(row: CompletedQueryItem): Action[] {
    const actions: Action[] = [
      { label: 'Cover Page', key: 'cover-page' },
      { label: 'Report', key: 'report' },
    ];
    if (row.isOwner) {
      actions.push({ label: 'Delete', key: 'delete' });
    }
    return actions;
  }

  async ngOnInit(): Promise<void> {
    this.dispose.subs = this.requestedWithinControl.valueChanges.subscribe(
      () => {
        void this.loadCompletedQueries(false);
      },
    );

    await this.loadCompletedQueries(true);
  }

  async loadCompletedQueries(showLoading = true): Promise<void> {
    if (showLoading) {
      this.loading = true;
    }
    try {
      const request: CompletedQueryListRequest = {
        requestedWithin: this.requestedWithinControl.value ?? 'all',
      };
      const response = await this.api.query.getCompletedQueries(request);
      this.completedQueries = response.items.map((row) => ({
        ...row,
        completedRowKey: `${row.completedQueryId}-${row.fileNumber}`,
      }));
      this.totalCount = response.paging.totalCount;
      this.retentionNotice = response.retentionNotice;
      this.selectedQueries = [];
    } finally {
      this.loading = false;
    }
  }

  onSelectionChange(selected: CompletedQueryItem[]): void {
    this.selectedQueries = selected;
  }

  search(): void {
    void this.loadCompletedQueries(false);
  }

  openRowMenu(event: Event, row: CompletedQueryItem): void {
    this.rowMenuItems = this.getRowActions(row);
    this.rowMenu.items = this.rowMenuItems;
    this.rowMenu.toggle(event, row);
  }

  onRowAction(action: Action): void {
    const query = (this.rowMenu?.data ?? action.data) as CompletedQueryItem;
    if (!query) return;

    switch (action.key) {
      case 'cover-page':
        void this.downloadCoverPage(query);
        break;
      case 'report':
        void this.downloadResult(query);
        break;
      case 'delete':
        this.deleteQuery(query);
        break;
    }
  }

  async downloadResult(query: CompletedQueryItem): Promise<void> {
    try {
      const blob = await this.api.query.downloadResult(
        query.completedQueryId,
        query.fileNumber,
      );
      const fallbackName = `${query.name}.${query.format ?? 'pdf'}`;
      this.downloadBlob(blob, query.downloadFileName || fallbackName);
    } catch {
      this.snackbar.show(
        QueryCompletedComponent.invalidServerFileMessage,
        'Error',
        'fa-light fa-circle-exclamation',
        6000,
      );
    }
  }

  async downloadCoverPage(query: CompletedQueryItem): Promise<void> {
    try {
      const blob = await this.api.query.downloadCoverPage(
        query.completedQueryId,
        query.fileNumber,
      );
      this.downloadBlob(blob, 'Cover_Page.txt');
    } catch {
      this.snackbar.show(
        QueryCompletedComponent.invalidServerFileMessage,
        'Error',
        'fa-light fa-circle-exclamation',
        6000,
      );
    }
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  deleteQuery(query: CompletedQueryItem): void {
    this.confirm.show({
      label: 'Delete Completed Query',
      message: `Delete completed query "${query.name}"?`,
      confirm: async () => {
        await this.api.query.deleteCompletedQuery(
          query.completedQueryId,
          query.fileNumber,
        );
        await this.loadCompletedQueries();
      },
    });
  }

  deleteSelected(): void {
    const count = this.selectedOwnedQueries.length;
    this.confirm.show({
      label: 'Delete Completed Queries',
      message: `Delete ${count} selected completed ${count === 1 ? 'query' : 'queries'}?`,
      confirm: async () => {
        for (const query of this.selectedOwnedQueries) {
          await this.api.query.deleteCompletedQuery(
            query.completedQueryId,
            query.fileNumber,
          );
        }
        this.selectedQueries = [];
        await this.loadCompletedQueries();
      },
    });
  }
}
